import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import RNGChart from './components/RNGChart';

function DCLPurchaseModal({
  deal,
  quantity = 1,
  onClose,
  onConfirm,
  onDone,
  onError
}) {
  const [status, setStatus] = useState('confirm');
  const [result, setResult] = useState(null);
  const [approvedCollections, setApprovedCollections] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);

  // Fetch approved collections on mount
  useEffect(() => {
    const fetchCollections = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin?action=dcl-collections`);
        const data = await response.json();
        setApprovedCollections(data.collections || []);
      } catch (error) {
        console.error('Failed to fetch approved collections:', error);
      }
    };
    fetchCollections();
  }, []);

  // Helper function to get collection item title by collection address and item ID
  const getCollectionItemTitle = (address, itemId) => {
    const collectionItem = approvedCollections.find(
      item => item.collection_address.toLowerCase() === address?.toLowerCase() && 
              item.urn_item_id === itemId
    );
    return collectionItem?.title || 'Unknown Item';
  };

  // Helper function to get collection title (fallback)
  const getCollectionTitle = (address) => {
    const collection = approvedCollections.find(
      col => col.collection_address.toLowerCase() === address?.toLowerCase()
    );
    return collection?.title || 'Unknown Collection';
  };

  // Disable body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Handle keyboard events
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && status === 'confirm') {
        handleCancel();
      }
      // Prevent ESC during processing or completion
      if (status === 'processing' && e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [status]);

  const handleConfirm = async () => {
    setStatus('processing');
    try {
      const purchaseResult = await onConfirm();
      setResult(purchaseResult);
      setStatus('complete');
    } catch (err) {
      onClose();
      throw err;
    }
  };

  const handleCancel = () => {
    // Show the same nice cancellation message as MetaMask cancellations
    if (onError) {
      onError({
        title: 'Purchase Cancelled',
        message: 'You cancelled the purchase. No charges were made to your wallet.',
        type: 'info'
      });
    }
    onClose();
  };

  const handleDone = () => {
    if (onDone) {
      onDone();
    } else {
      onClose();
    }
  };

  // Calculate total price from all deal items (support for bundles)
  const dealItems = deal.deal_items && deal.deal_items.length > 0 ? deal.deal_items : 
    [{ dcl_price_mana: deal.dcl_price_mana, dcl_collection_address: deal.dcl_collection_address, dcl_item_id: deal.dcl_item_id, deal_image: deal.deal_image }];
  
  // If RNG is enabled, only charge for one item
  const bundlePrice = deal.rng_enabled ? 
    parseFloat(dealItems[0].dcl_price_mana) : 
    dealItems.reduce((sum, item) => sum + (parseFloat(item.dcl_price_mana) || 0), 0);
  
  const totalPrice = bundlePrice * quantity;
  const totalItems = dealItems.length;

  const modalContent = (
    <div 
      className="modal-overlay" 
      style={{ 
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 999999,
        backdropFilter: 'blur(5px)',
        isolation: 'isolate',
        contain: 'layout style paint'
      }}
             onClick={(e) => {
         // Only allow closing by clicking outside during confirm stage
         if (status === 'confirm') {
           handleCancel();
         }
         // Prevent any clicks during processing or completion
         e.stopPropagation();
       }}
    >
      <div 
        className="dcl-purchase-modal-content"
        style={{
          position: 'relative',
          backgroundColor: '#1a1a1a',
          border: '2px solid rgba(138, 43, 226, 0.5)',
          borderRadius: '20px',
          padding: '2rem',
          maxWidth: '800px',
          width: '90%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5), 0 0 40px rgba(138, 43, 226, 0.3)',
          color: 'white'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {status === 'confirm' && (
          <>
            <h2 style={{ color: '#8a2be2', marginBottom: '1rem' }}>
              🎮 Purchase DCL Wearable
            </h2>
            
            {/* Deal Items */}
            <div style={{
              backgroundColor: 'rgba(138, 43, 226, 0.1)',
              border: '1px solid rgba(138, 43, 226, 0.3)',
              borderRadius: '10px',
              padding: '1rem',
              marginBottom: '1rem'
            }}>
              <h3 style={{ color: '#8a2be2', margin: '0 0 1rem 0', textAlign: 'center' }}>
                {deal.deal_name}
              </h3>
              
              {/* RNG Info */}
              {deal.rng_enabled && deal.rng_pool && (
                <div style={{ 
                  backgroundColor: 'rgba(138, 43, 226, 0.2)',
                  padding: '1rem',
                  borderRadius: '8px',
                  marginBottom: '1rem'
                }}>
                  <h4 style={{ color: '#8a2be2', margin: '0 0 0.5rem 0' }}>🎲 Random Reward Selection</h4>
                  {deal.deal_items.map((item, i) => (
                    <div key={i} style={{ 
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '0.5rem',
                      padding: '0.5rem',
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: '4px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        {/* Item Image */}
                        {item.deal_image && (
                          <img
                            src={item.deal_image}
                            alt={`Item ${i + 1}`}
                            style={{
                              width: '40px',
                              height: '40px',
                              borderRadius: '8px',
                              border: '1px solid #8a2be2',
                              objectFit: 'cover'
                            }}
                          />
                        )}
                        <div>
                          <div style={{ fontSize: '0.9rem', color: '#fff' }}>
                            <strong>{getCollectionItemTitle(item.dcl_collection_address, item.dcl_item_id)}</strong>
                          </div>
                          <div style={{ fontSize: '0.8rem', color: '#ccc', fontFamily: 'monospace' }}>
                            {item.dcl_collection_address?.slice(0, 8)}...{item.dcl_collection_address?.slice(-6)}
                          </div>
                        </div>
                      </div>
                      <div style={{ fontSize: '0.9rem', color: '#8a2be2', fontWeight: 'bold' }}>
                        {deal.rng_pool[i]}% chance
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Show all items in the bundle only if not RNG enabled */}
              {!deal.rng_enabled && dealItems.map((item, index) => (
                <div key={index} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '0.75rem',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '8px',
                  marginBottom: index < dealItems.length - 1 ? '0.75rem' : 0
                }}>
                  {/* Item Image */}
                  {item.deal_image && (
                    <img
                      src={item.deal_image}
                      alt={`Item ${index + 1}`}
                      style={{
                        width: '60px',
                        height: '60px',
                        borderRadius: '8px',
                        border: '1px solid #8a2be2',
                        objectFit: 'cover'
                      }}
                    />
                  )}
                  
                  {/* Item Details */}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '0.9rem', color: '#fff', marginBottom: '0.25rem' }}>
                      <strong>Item #{item.dcl_item_id}</strong>
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#ccc', fontFamily: 'monospace' }}>
                      {item.dcl_collection_address?.slice(0, 8)}...{item.dcl_collection_address?.slice(-6)}
                    </div>
                  </div>
                  
                  {/* Item Price */}
                  <div style={{ fontSize: '0.9rem', color: '#8a2be2', fontWeight: 'bold' }}>
                    {item.dcl_price_mana} MANA
                  </div>
                </div>
              ))}
              
              {/* Bundle Summary - only show if not RNG enabled */}
              {!deal.rng_enabled && (
                <div style={{ 
                  borderTop: '1px solid rgba(138, 43, 226, 0.3)', 
                  marginTop: '1rem', 
                  paddingTop: '1rem',
                  textAlign: 'center' 
                }}>
                  <p style={{ margin: '0.5rem 0', fontSize: '1.1rem' }}>
                    <strong>Bundle Quantity:</strong> {quantity}
                  </p>
                  <p style={{ margin: '0.5rem 0', fontSize: '1.1rem' }}>
                    <strong>Items per Bundle:</strong> {totalItems}
                  </p>
                  <p style={{ margin: '0.5rem 0', fontSize: '1.2rem', color: '#8a2be2' }}>
                    <strong>Total:</strong> {totalPrice} MANA
                  </p>
                </div>
              )}

              {/* RNG Summary - only show if RNG enabled */}
              {deal.rng_enabled && (
                <div style={{ 
                  borderTop: '1px solid rgba(138, 43, 226, 0.3)', 
                  marginTop: '1rem', 
                  paddingTop: '1rem',
                  textAlign: 'center' 
                }}>
                  <p style={{ margin: '0.5rem 0', fontSize: '1.1rem' }}>
                    <strong>Quantity:</strong> {quantity}
                  </p>
                  <p style={{ margin: '0.5rem 0', fontSize: '1.2rem', color: '#8a2be2' }}>
                    <strong>Price per Roll:</strong> {dealItems[0].dcl_price_mana} MANA
                  </p>
                  <p style={{ margin: '0.5rem 0', fontSize: '1.2rem', color: '#8a2be2' }}>
                    <strong>Total:</strong> {(parseFloat(dealItems[0].dcl_price_mana) * quantity).toFixed(2)} MANA
                  </p>
                </div>
              )}
            </div>

            <p style={{ textAlign: 'center', marginBottom: '1.5rem', fontSize: '0.9rem', opacity: 0.9 }}>
              {deal.rng_enabled ? (
                <>
                  This will transfer <strong>{totalPrice} MANA</strong> from your wallet and mint <strong>{quantity} random DCL Wearable{quantity > 1 ? 's' : ''}</strong> to your address.
                  <br />
                  <span style={{ fontSize: '0.8rem' }}>
                    ({quantity} roll{quantity > 1 ? 's' : ''})
                  </span>
                </>
              ) : (
                <>
                  This will transfer <strong>{totalPrice} MANA</strong> from your wallet and mint <strong>{quantity * totalItems} DCL Wearable{(quantity * totalItems) > 1 ? 's' : ''}</strong> to your address.
                  {totalItems > 1 && (
                    <>
                      <br />
                      <span style={{ fontSize: '0.8rem' }}>
                        ({quantity} bundle{quantity > 1 ? 's' : ''} × {totalItems} item{totalItems > 1 ? 's' : ''} each)
                      </span>
                    </>
                  )}
                </>
              )}
            </p>

            <div className="modal-buttons" style={{ marginTop: '1rem' }}>
              <button
                className="no-btn"
                style={{ backgroundColor: '#e74c3c', color: '#fff' }}
                onClick={handleCancel}
              >
                Cancel
              </button>
              <button
                className="rainbowBtn"
                style={{ backgroundColor: '#8a2be2', color: '#fff' }}
                onClick={handleConfirm}
              >
                🔥 Purchase with MANA
              </button>
            </div>
          </>
        )}

        {status === 'processing' && (
          <>
            <h2 style={{ color: '#8a2be2' }}>⏳ Processing Purchase...</h2>
            <div style={{
              textAlign: 'center',
              padding: '2rem',
              fontSize: '1.1rem'
            }}>
              <div style={{ marginBottom: '1rem' }}>
                {deal.rng_enabled ? (
                  <>
                    Sending {totalPrice} MANA and minting {quantity} random DCL Wearable{quantity > 1 ? 's' : ''}...
                  </>
                ) : (
                  <>
                    Sending {totalPrice} MANA and minting {quantity * totalItems} DCL Wearable{(quantity * totalItems) > 1 ? 's' : ''}...
                  </>
                )}
              </div>
              <div className="loading-spinner" style={{
                border: '4px solid rgba(138, 43, 226, 0.3)',
                borderTop: '4px solid #8a2be2',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                animation: 'spin 2s linear infinite',
                margin: '0 auto'
              }}>
              </div>
              <style>
                {`
                  @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                  }
                `}
              </style>
              <div style={{ marginTop: '1rem', fontSize: '0.9rem', opacity: 0.8 }}>
                Please do not close this window...
              </div>
            </div>
          </>
        )}

        {status === 'complete' && (
          <>
            <h2 style={{ color: '#28a745' }}>✅ Purchase Successful!</h2>
            <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              {/* RNG Chart for RNG-enabled deals */}
              {deal.rng_enabled && result?.random_value && result?.rng_pool && result?.minted_item && (
                <RNGChart
                  randomValue={result.random_value}
                  rewards={result.rng_pool}
                  selectedReward={result.minted_item}
                  totalChance={result.rng_pool.reduce((sum, r) => sum + (Number(r.chance) || 0), 0)}
                  style={{ marginBottom: '2rem' }}
                />
              )}
              
              {deal.rng_enabled ? (
                // Simplified display for RNG deals
                <div style={{ textAlign: 'center' }}>
                  <p style={{ marginBottom: '0.2rem', fontSize: '0.9rem', color: '#ccc' }}>
                    Collection: {result?.minted_item?.dcl_collection_address || result?.collection}
                  </p>
                  <p style={{ marginBottom: '0.3rem', fontSize: '0.9rem', color: '#ccc' }}>
                    Item: {result?.minted_item?.item_title || result?.minted_item?.collection_title}
                  </p>
                </div>
              ) : (
                <>
                  <p style={{ fontSize: '1.1rem', marginBottom: '1rem' }}>
                    {quantity * totalItems} DCL Wearable{(quantity * totalItems) > 1 ? 's' : ''} purchased successfully!
                  </p>
                  {/* Show all minted items */}
                  <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: '0.75rem',
                    marginTop: '1rem',
                    padding: '1rem',
                    backgroundColor: 'rgba(138, 43, 226, 0.1)',
                    borderRadius: '8px'
                  }}>
                    {deal.deal_items.map((item, index) => (
                      <div key={index} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                        padding: '0.75rem',
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        borderRadius: '8px'
                      }}>
                        {/* Item Image */}
                        {item.deal_image && (
                          <img
                            src={item.deal_image}
                            alt={`Item ${index + 1}`}
                            style={{
                              width: '60px',
                              height: '60px',
                              borderRadius: '8px',
                              border: '1px solid #8a2be2',
                              objectFit: 'cover'
                            }}
                          />
                        )}
                        
                        {/* Item Details */}
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '0.9rem', color: '#fff', marginBottom: '0.25rem' }}>
                            <strong>{getCollectionTitle(item.dcl_collection_address)}</strong>
                          </div>
                          <div style={{ fontSize: '0.8rem', color: '#ccc', fontFamily: 'monospace' }}>
                            {item.dcl_collection_address?.slice(0, 8)}...{item.dcl_collection_address?.slice(-6)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
              
              {/* Transaction Links Section */}
              <div style={{ 
                marginTop: '1rem', 
                padding: '0.75rem', 
                backgroundColor: 'rgba(255, 255, 255, 0.05)', 
                borderRadius: '8px',
                fontSize: '0.85rem'
              }}>
                {result?.paymentTxHash && (
                  <div style={{ marginBottom: '0.3rem' }}>
                    <strong>Payment Transaction:</strong>{' '}
                    <a
                      href={`https://polygonscan.com/tx/${result.paymentTxHash}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#8a2be2', textDecoration: 'underline' }}
                    >
                      View on PolygonScan
                    </a>
                  </div>
                )}
                
                {/* Show all mint transactions */}
                {result?.mintTxHashes && result.mintTxHashes.length > 0 ? (
                  result.mintTxHashes.length === 1 ? (
                    <div>
                      <strong>Mint Transaction:</strong>{' '}
                      <a
                        href={`https://polygonscan.com/tx/${result.mintTxHashes[0]}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#8a2be2', textDecoration: 'underline' }}
                      >
                        View on PolygonScan
                      </a>
                    </div>
                  ) : (
                    <div>
                      <strong>Mint Transactions:</strong>
                      <div style={{ marginTop: '0.25rem' }}>
                        {result.mintTxHashes.map((txHash, index) => (
                          <div key={txHash} style={{ marginBottom: '0.2rem' }}>
                            <span style={{ fontSize: '0.8rem' }}>Collection {index + 1}:</span>{' '}
                            <a
                              href={`https://polygonscan.com/tx/${txHash}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: '#8a2be2', textDecoration: 'underline' }}
                            >
                              View on PolygonScan
                            </a>
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                ) : null}
              </div>
            </div>
            
            <button 
              className="rainbowBtn" 
              onClick={handleDone}
              style={{ 
                width: '100%', 
                padding: '0.5rem 1rem',
                fontSize: '0.9rem'
              }}
            >
              Awesome!
            </button>
          </>
        )}
             </div>
     </div>
   );

   return createPortal(modalContent, document.body);
 }
 
 export default DCLPurchaseModal; 